import React, { useCallback } from 'react';
import { Pressable, View } from 'react-native';
import { useMutation } from '@apollo/client';
import { Alert } from '../../components/other/Alert';

import { getUserName } from '../../../common/utils/user/getUserName';
import { useUserContext } from '../../../common/utils/context';
import { ROLE_PERMISSIONS } from '../../../common/constants';
import { DESTROY_NOTE } from '../../../common/graphql/note';
import { formatDate } from '../../../common/utils/utils';

import { Text } from '../../components';
import { SecondaryButton, Tag } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const NoteListItem = ({
	item: note,
	customProps,
}) => {
	const { hasPermission } = useUserContext();
	const canManageNotes = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_NOTES);
	const styles = useThemedStyles(createStyles);

	const [destroyNote] = useMutation(DESTROY_NOTE);

	const onClickDelete = () => {

		Alert.alert(
			'Are you sure?',
			'',
			[
				{ text: 'Cancel', onPress: () => { }, style: 'cancel' },
				{
					text: 'Delete',
					onPress: () => {
						// eslint-disable-next-line promise/catch-or-return
						destroyNote({
							variables: {
								id: note.id,
							},
						}).then(() => {
							if (customProps && customProps.refetchList) {
								customProps.refetchList();
							}
						});
					},
				},
			],
			{ cancelable: false },
		);
	};

	const handleOnClickEditNote = useCallback(async () => {
		await customProps.onClickEditNote(note);
	}, [customProps, note]);

	return (
		<View style={styles.card}>
			<View style={styles.topRow}>
				<View style={styles.meta}>
					{Boolean(note.isImportant) && (
						<Tag label="Important" tone="danger" />
					)}
					{Boolean(note.creator) && (
						<Text style={styles.smallText}>
							{formatDate(note.created, 'DAY_MONTHWORD_YEAR_HOUR_MINUTE_AMPM')}
						</Text>
					)}
					{Boolean(note.creator) && (
						<Text style={styles.smallText}>
							{ getUserName(note.creator) }
						</Text>
					)}
				</View>
			</View>
			<Text style={styles.description}>
				{note.description}
			</Text>
			{
				// Edit is the main action; Delete is a quiet destructive text button.
				canManageNotes && (
					<View style={styles.actions}>
						<SecondaryButton
							label="Edit note"
							icon="edit-2"
							onPress={handleOnClickEditNote}
							style={styles.editButton}
						/>
						<Pressable
							onPress={onClickDelete}
							accessibilityRole="button"
							accessibilityLabel="Delete note"
							style={({ pressed }) => [styles.deleteButton, pressed && styles.pressed]}
						>
							<Text style={styles.deleteLabel}>Delete</Text>
						</Pressable>
					</View>
				)
			}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius, touch } }) => ({
	card: {
		marginHorizontal: space.gutter,
		marginBottom: space.md,
		padding: 14,
		gap: 10,
		borderRadius: radius.hero,
		backgroundColor: colors.card,
	},
	topRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: space.sm,
	},
	meta: {
		flex: 1,
		gap: 2,
	},
	smallText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	pressed: {
		opacity: 0.7,
	},
	description: {
		...type.sectionTitle,
		color: colors.text,
	},
	actions: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.sm,
	},
	editButton: {
		minWidth: 140,
	},
	deleteButton: {
		minHeight: touch.min,
		justifyContent: 'center',
		paddingHorizontal: space.md,
		marginRight: -space.md,
	},
	deleteLabel: {
		...type.bodySmallStrong,
		color: colors.danger,
	},
});

export default NoteListItem;
